import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { ProfileHostPorts } from '../../services/profileHost';
import { t } from '../../services/locale';
import type { ProfileBooking } from './profileBooking.types';
import type { ProfileBranchDocument, ProfileDocumentRow, ProfileIdentity, ProfileUserDocument } from './profileAccount.types';
import type { ProfileLinkedAccount } from './ProfileLinkedAccounts';
import type { ProfilePaymentMethodKind } from './ProfilePaymentMethods';
import type { ProfileDialogData } from './ProfileDialog';
import {
  cancelMembership,
  confirmCancelBooking,
  confirmDeletePayment,
  goPayg,
  manageMembership,
  openAgreement,
  pauseMembership,
  reloadMember,
  saveProfileChanges,
  showChangePassword,
  showCreateLinkedAccount,
  showFirstImageDialog,
  showPaymentCollector,
  showRestrictedDialog,
  showSmsWaiver,
  showStudioWaiver,
  switchToChildAccount,
  switchToParentAccount,
} from './profileActions';
import { projectProfileDialogData, type ProfileViewActions, type ProfileViewState } from './projectProfileDialogData';
import {
  asBranch,
  asUser,
  buildMailTo,
  cloneUser,
  familyRole,
  readBookings,
  readCancelIds,
  readConsentChannels,
  readCredits,
  readDocumentList,
  readLinkedAccounts,
  readMemberships,
  readTransactions,
  type ProfileCloseResult,
  type ProfileRuntimeUser,
  type RuntimeMembership,
} from './profileRuntime';

const SPOT_BOOKING_FLAG = 'is-interactive-spot-booking-enabled';
const CLICK_TO_CANCEL_FLAG = 'is-click-to-cancel-email-request-enabled';
const RESTRICT_PROFILE_FLAG = 'is-restrict-personal-information-change-enabled';
const EMERGENCY_CONTACT_FLAG = 'is-booking-blocked-by-emergency-contact-enabled';

export type OrchestratedProfileDialogData = {
  orchestrated: true;
  profileDialogEnabled?: boolean;
  embedInHostModal?: boolean;
  host: ProfileHostPorts;
  onDismiss: (result?: ProfileCloseResult) => void;
};

export function isOrchestratedProfileData(
  data: object | null | undefined
): data is OrchestratedProfileDialogData {
  return !!data && 'orchestrated' in data && (data as { orchestrated?: boolean }).orchestrated === true;
}

function emptyState(embedInHostModal?: boolean): ProfileViewState {
  return {
    user: null,
    branch: null,
    profile: null,
    tab: 'my-bookings',
    editProfile: false,
    isAddressExpanded: false,
    bookings: [],
    bookingsLoaded: false,
    showContactAdminMessage: false,
    isInteractiveSpotBookingEnabled: false,
    creditsAvailable: null,
    credits: [],
    stackedMemberships: [],
    transactions: [],
    totalTransactions: 0,
    moreTransactions: false,
    linkedAccounts: [],
    isChildAccount: false,
    isGuardAccount: false,
    userDocuments: null,
    branchDocuments: null,
    userImagePreviewUrl: null,
    imageVersion: 1,
    hasUploadedProfileImage: false,
    saveErrors: [],
    showSaveErrors: false,
    isInputDisabled: false,
    userConsents: [],
    hasSmsWaiver: false,
    mailToContactStudio: '',
    cancellationAlertText: '',
    autoBookingIsEnabled: false,
    isClickToCancelEnabled: false,
    updateProfileRestricted: false,
    mandatoryEmergencyContact: false,
    embedInHostModal,
  };
}

function isHttpUrl(url: string): boolean {
  if (url.startsWith('https://')) {
    return true;
  }
  return url.startsWith('http://');
}

async function imageExists(url: string | null | undefined): Promise<boolean> {
  if (!url) {
    return false;
  }
  if (!isHttpUrl(url)) {
    return false;
  }
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.status === 200;
  } catch {
    return false;
  }
}

function focusProfilePane(editing: boolean): void {
  const selector = editing
    ? '.modal-body-left-editing input'
    : '.modal-body-left-notediting button';
  queueMicrotask(() => {
    const target = document.querySelector(selector);
    if (target instanceof HTMLElement) {
      target.focus();
    }
  });
}

type ProfilePatch = (partial: Partial<ProfileViewState>) => void;

type ProfileSession = {
  host: ProfileHostPorts;
  stateRef: { current: ProfileViewState };
  patch: ProfilePatch;
  setState: Dispatch<SetStateAction<ProfileViewState>>;
  previewUrl: { current: string | null };
  imageFile: { current: File | null };
  imageVersion: { current: number };
  dismiss: (result: ProfileCloseResult) => void;
  refresh: () => void;
  replaceProfileFromUser: (user: ProfileRuntimeUser | null) => void;
  loadMemberships: (branchId: string, userId: string) => Promise<void>;
  loadTransactions: (userId: string, replace: boolean) => Promise<void>;
  loadBookings: (userId: string) => Promise<void>;
  transactionPage: { current: number };
  spotFlagRef: { current: boolean };
};

function replaceStoredProfile(
  user: ProfileRuntimeUser | null,
  previewUrl: { current: string | null },
  imageVersion: { current: number },
  patch: ProfilePatch
): void {
  if (previewUrl.current) {
    URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = null;
  }
  const profile = user ? cloneUser(user) : null;
  imageVersion.current += 1;
  patch({
    user,
    profile,
    userImagePreviewUrl: null,
    imageVersion: imageVersion.current,
    showSaveErrors: false,
  });
  void imageExists(profile?.image_url).then((exists) => {
    patch({ hasUploadedProfileImage: exists });
  });
}

async function loadProfileBookings(
  host: ProfileHostPorts,
  userId: string,
  spotFlagRef: { current: boolean },
  patch: ProfilePatch
): Promise<void> {
  const spotBooking = host.isFeatureFlagEnabled(SPOT_BOOKING_FLAG);
  spotFlagRef.current = spotBooking;
  patch({ isInteractiveSpotBookingEnabled: spotBooking });
  try {
    const response = readBookings(await host.getBookingsById(userId, spotBooking));
    patch({
      bookings: response.bookings.map((booking) => ({
        ...booking,
        bookedSpotName: host.resolveBookedSpotName(booking),
        isSpotBookingEnabled: host.isSpotBookingEnabledForBooking(booking),
      })),
      showContactAdminMessage: response.hasMore,
      bookingsLoaded: true,
    });
  } catch {
    patch({ bookingsLoaded: true });
  }
}

async function loadProfileMemberships(
  host: ProfileHostPorts,
  branchId: string,
  userId: string,
  patch: ProfilePatch
): Promise<void> {
  try {
    const memberships = readMemberships(
      await host.getUsersStackedMemberships(branchId, userId),
      readCancelIds(host.getCancelRequestIds())
    );
    patch({ stackedMemberships: memberships });
  } catch {
    patch({ stackedMemberships: [] });
  }
}

type TransactionLoad = {
  host: ProfileHostPorts;
  userId: string;
  replace: boolean;
  transactionPage: { current: number };
  setState: Dispatch<SetStateAction<ProfileViewState>>;
};

async function loadProfileTransactions(input: TransactionLoad): Promise<void> {
  const page = input.replace ? 1 : input.transactionPage.current;
  try {
    const response = readTransactions(await input.host.getTransactionsById(input.userId, page));
    input.transactionPage.current = page + 1;
    input.setState((current) => ({
      ...current,
      totalTransactions: response.total,
      moreTransactions: response.hasMore,
      transactions: input.replace ? response.rows : [...current.transactions, ...response.rows],
    }));
  } catch {
    return;
  }
}

function profileFlagPatch(
  host: ProfileHostPorts,
  branch: ReturnType<typeof asBranch>,
  role: ReturnType<typeof familyRole>
): Partial<ProfileViewState> {
  return {
    autoBookingIsEnabled: !!branch?.features?.booking?.auto_booking_enabled,
    isClickToCancelEnabled: host.isFeatureFlagEnabled(CLICK_TO_CANCEL_FLAG),
    updateProfileRestricted: host.isFeatureFlagEnabled(RESTRICT_PROFILE_FLAG),
    mandatoryEmergencyContact: host.isFeatureFlagEnabled(EMERGENCY_CONTACT_FLAG),
    isChildAccount: role.isChild,
    isGuardAccount: role.isGuard,
  };
}

function applyStudioContact(
  host: ProfileHostPorts,
  branch: NonNullable<ReturnType<typeof asBranch>>,
  user: ProfileRuntimeUser,
  patch: ProfilePatch
): void {
  void host.getBranchById(branch._id as string).then((response) => {
    const email = typeof (response as { email?: string } | null)?.email === 'string'
      ? (response as { email: string }).email
      : '';
    if (!email) {
      return;
    }
    if (!user.name) {
      return;
    }
    patch({
      cancellationAlertText: t('WEB_CANCEL_MEMBERSHIP_FTC_REQUESTED_ALERT_TEXT', { studioEmail: email }),
      mailToContactStudio: buildMailTo(email, user.name, user.membership?.membership_name ?? '', branch.name ?? ''),
    });
  }).catch(() => undefined);
}

function loadBranchDocuments(
  host: ProfileHostPorts,
  branchId: string,
  userId: string,
  patch: ProfilePatch
): void {
  void host.getBranchDocuments(branchId).then((response) => {
    patch({ branchDocuments: readDocumentList<ProfileBranchDocument>(response) });
  }).catch(() => undefined);
  void host.getDocumentsByUserId(branchId, userId).then((response) => {
    patch({ userDocuments: readDocumentList<ProfileUserDocument>(response) });
  }).catch(() => undefined);
  void host.getTermsAndConditions(branchId, 'SMS_WAIVER').then((response) => {
    const waiver = Array.isArray(response) ? response[0] : null;
    patch({ hasSmsWaiver: waiver != null });
  }).catch(() => undefined);
}

function refreshProfile(session: ProfileSession): void {
  const user = asUser(session.host.getCurrentUser());
  const branch = asBranch(session.host.getCurrentBranch());
  session.host.fetchAllFeatureFlags();
  session.host.scrollIframeToTop();
  const role = familyRole(user, branch);
  session.transactionPage.current = 1;
  session.setState((current) => ({
    ...current,
    user,
    branch,
    tab: current.tab,
    ...profileFlagPatch(session.host, branch, role),
    bookingsLoaded: false,
  }));
  session.replaceProfileFromUser(user);
  loadProfileResources(session, user, branch, role);
}

function applyCredits(session: ProfileSession, userId: string): void {
  void session.host.getAvailableCreditsById(userId).then((response) => {
    const credits = readCredits(response);
    session.patch({ creditsAvailable: credits.total, credits: credits.credits });
  }).catch(() => undefined);
}

function applyLinkedAccounts(session: ProfileSession, userId: string): void {
  void session.host.getLinkedAccount(userId).then((response) => {
    session.patch({ linkedAccounts: readLinkedAccounts(response) });
  }).catch(() => undefined);
}

function applyConsents(session: ProfileSession, userId: string): void {
  void session.host.getUserConsents(userId).then((response) => {
    session.patch({
      userConsents: readConsentChannels(response, session.host.hydrateChannelConsentPurposeStatuses),
    });
  }).catch(() => undefined);
}

function loadBranchScoped(
  session: ProfileSession,
  branch: ReturnType<typeof asBranch>,
  user: ProfileRuntimeUser,
  userId: string
): void {
  if (!branch) {
    return;
  }
  if (!branch._id) {
    return;
  }
  void session.loadMemberships(branch._id, userId);
  loadBranchDocuments(session.host, branch._id, userId, session.patch);
  applyStudioContact(session.host, branch, user, session.patch);
}

function loadGuardAccounts(session: ProfileSession, role: ReturnType<typeof familyRole>, userId: string): void {
  if (!role.active) {
    return;
  }
  if (!role.isGuard) {
    return;
  }
  applyLinkedAccounts(session, userId);
}

function loadProfileResources(
  session: ProfileSession,
  user: ReturnType<typeof asUser>,
  branch: ReturnType<typeof asBranch>,
  role: ReturnType<typeof familyRole>
): void {
  if (!user) {
    session.patch({ bookingsLoaded: true });
    return;
  }
  if (!user._id) {
    session.patch({ bookingsLoaded: true });
    return;
  }
  void session.loadBookings(user._id);
  void session.loadTransactions(user._id, true);
  loadBranchScoped(session, branch, user, user._id);
  applyCredits(session, user._id);
  loadGuardAccounts(session, role, user._id);
  applyConsents(session, user._id);
}

function reloadBookingsIfSpotFlagChanged(session: ProfileSession): void {
  const user = asUser(session.host.getCurrentUser());
  if (!user?._id) {
    return;
  }
  const resolved = session.host.isFeatureFlagEnabled(SPOT_BOOKING_FLAG);
  if (resolved === session.spotFlagRef.current) {
    return;
  }
  void session.loadBookings(user._id);
}

function reloadMembershipsAfterCancelWatch(session: ProfileSession): void {
  const current = session.stateRef.current;
  if (!current.branch?._id) {
    return;
  }
  if (!current.user?._id) {
    return;
  }
  void session.loadMemberships(current.branch._id, current.user._id);
}

function subscribeProfile(session: ProfileSession): () => void {
  session.refresh();
  const stopUser = session.host.onUserChange(() => {
    session.refresh();
  });
  const stopFlags = session.host.onFeatureFlagsLoaded(() => {
    reloadBookingsIfSpotFlagChanged(session);
  });
  let skipInitialCancelWatch = true;
  const stopCancelWatch = session.host.onCancelRequestIdsChange(() => {
    if (skipInitialCancelWatch) {
      skipInitialCancelWatch = false;
      return;
    }
    reloadMembershipsAfterCancelWatch(session);
  });
  return () => {
    stopUser();
    stopFlags();
    stopCancelWatch();
    if (session.previewUrl.current) {
      URL.revokeObjectURL(session.previewUrl.current);
    }
  };
}

function pauseAndReload(session: ProfileSession, membership: RuntimeMembership): void {
  const branchId = session.stateRef.current.branch?._id;
  const userId = session.stateRef.current.user?._id;
  void pauseMembership(session.host, membership, () => {
    if (!branchId) {
      return;
    }
    if (!userId) {
      return;
    }
    void session.loadMemberships(branchId, userId);
  });
}

function navigationActions(session: ProfileSession): Pick<ProfileViewActions, 'changeTab' | 'openFamilyAccounts' | 'logOut'> {
  return {
    changeTab: (tab: string) => {
      if (!tab) {
        return;
      }
      if (tab === session.stateRef.current.tab) {
        return;
      }
      session.patch({ tab });
      session.host.triggerAnalytics({ category: tab, name: 'PROFILE_TAB_NAV' });
    },
    logOut: () => {
      void session.host.logOut();
      session.dismiss({ success: false, redirect: false });
    },
    openFamilyAccounts: () => {
      if (session.stateRef.current.tab === 'linked-accounts') {
        return;
      }
      session.patch({ tab: 'linked-accounts' });
      session.host.triggerAnalytics({ category: 'linked-accounts', name: 'PROFILE_TAB_NAV' });
    },
  };
}

function saveProfileEdits(session: ProfileSession): void {
  const current = session.stateRef.current;
  if (!current.user?._id) {
    return;
  }
  if (!current.profile) {
    return;
  }
  void saveProfileChanges({
    host: session.host,
    user: current.user,
    branch: current.branch,
    profile: current.profile,
    consents: current.userConsents,
    image: session.imageFile.current,
  }).then((result) => {
    if (result.ok === false) {
      session.patch({ showSaveErrors: true, saveErrors: result.errors });
      focusProfilePane(true);
      return;
    }
    session.imageFile.current = null;
    session.patch({ editProfile: false, showSaveErrors: false, saveErrors: [] });
    session.replaceProfileFromUser(result.user);
    focusProfilePane(false);
  });
}

function updateConsent(
  session: ProfileSession,
  channelKey: string,
  purposeKey: string,
  next: boolean
): void {
  session.setState((current) => ({
    ...current,
    userConsents: current.userConsents.map((channel) => {
      if (channel.key !== channelKey) {
        return channel;
      }
      const purpose = channel[purposeKey];
      if (!purpose) {
        return channel;
      }
      if (typeof purpose !== 'object') {
        return channel;
      }
      return { ...channel, [purposeKey]: { ...purpose, status: next } };
    }),
  }));
}

function editActions(session: ProfileSession): Pick<
  ProfileViewActions,
  'startEdit' | 'toggleAddress' | 'patchProfile' | 'patchAddress' | 'selectImage' | 'save' | 'changePassword' | 'showRestricted' | 'showFirstImage' | 'setConsent' | 'openSmsWaiver'
> {
  return {
    startEdit: () => {
      session.patch({ editProfile: true });
      session.host.triggerAnalytics({ category: 'profile-dialogue', name: 'PROFILE_EDIT' });
    },
    toggleAddress: () => session.patch({ isAddressExpanded: !session.stateRef.current.isAddressExpanded }),
    patchProfile: (profilePatch: Partial<ProfileIdentity>) => {
      session.setState((current) => ({
        ...current,
        profile: { ...current.profile, ...profilePatch },
      }));
    },
    patchAddress: (addressPatch: Partial<NonNullable<ProfileIdentity['address']>>) => {
      session.setState((current) => ({
        ...current,
        profile: {
          ...current.profile,
          address: { ...current.profile?.address, ...addressPatch },
        },
      }));
    },
    selectImage: (file: File | null) => {
      if (session.previewUrl.current) {
        URL.revokeObjectURL(session.previewUrl.current);
        session.previewUrl.current = null;
      }
      session.imageFile.current = file;
      if (file) {
        session.previewUrl.current = URL.createObjectURL(file);
      }
      session.patch({ userImagePreviewUrl: session.previewUrl.current });
    },
    save: () => saveProfileEdits(session),
    changePassword: () => {
      void showChangePassword(session.host);
    },
    showRestricted: () => {
      session.patch({ isInputDisabled: true });
      void showRestrictedDialog(session.host);
    },
    showFirstImage: () => {
      void showFirstImageDialog(session.host);
    },
    setConsent: (channelKey: string, purposeKey: string, next: boolean) => {
      updateConsent(session, channelKey, purposeKey, next);
    },
    openSmsWaiver: () => {
      void showSmsWaiver(session.host);
    },
  };
}

function requestCancelMembership(session: ProfileSession, membership: RuntimeMembership): void {
  const current = session.stateRef.current;
  if (!current.user) {
    return;
  }
  if (!current.branch) {
    return;
  }
  void cancelMembership({
    host: session.host,
    user: current.user,
    branch: current.branch,
    membership,
    onPause: () => pauseAndReload(session, membership),
  });
}

function requestManageMembership(session: ProfileSession, membership: RuntimeMembership): void {
  const current = session.stateRef.current;
  void manageMembership({
    host: session.host,
    user: current.user,
    membership,
    isClickToCancelEnabled: current.isClickToCancelEnabled,
    mailTo: current.mailToContactStudio,
    onCancel: () => requestCancelMembership(session, membership),
    onPause: () => pauseAndReload(session, membership),
  });
}

function membershipActions(session: ProfileSession): Pick<
  ProfileViewActions,
  'cancelBooking' | 'showMoreTransactions' | 'pauseMembership' | 'cancelMembership' | 'manageMembership' | 'buyMembership' | 'goPayg' | 'trackContactStudio'
> {
  return {
    cancelBooking: (booking: ProfileBooking) => {
      void confirmCancelBooking(session.host, session.stateRef.current.branch, session.stateRef.current.user?._id, booking);
    },
    showMoreTransactions: () => {
      const userId = session.stateRef.current.user?._id;
      if (userId) {
        void session.loadTransactions(userId, false);
      }
    },
    pauseMembership: (membership: RuntimeMembership) => {
      pauseAndReload(session, membership);
    },
    cancelMembership: (membership: RuntimeMembership) => {
      requestCancelMembership(session, membership);
    },
    manageMembership: (membership: RuntimeMembership) => {
      requestManageMembership(session, membership);
    },
    buyMembership: () => {
      session.dismiss({ success: true, redirect: true, redirectTo: 'memberships' });
    },
    goPayg: () => {
      const userId = session.stateRef.current.user?._id;
      if (!userId) {
        return;
      }
      void goPayg(session.host, userId).then((ok) => {
        if (ok) {
          session.refresh();
        }
      });
    },
    trackContactStudio: () => {
      session.host.triggerAnalytics({ category: 'profile-memberships', name: 'CONTACT_STUDIO' });
    },
  };
}

function accountActions(session: ProfileSession): Pick<
  ProfileViewActions,
  'useAccount' | 'addLinkedAccount' | 'switchToParent' | 'previewWaiver' | 'openDocument' | 'addOrReplacePayment' | 'deletePayment'
> {
  return {
    useAccount: (account: ProfileLinkedAccount) => {
      const parentId = session.stateRef.current.user?._id;
      if (!parentId) {
        return;
      }
      if (!account._id) {
        return;
      }
      void switchToChildAccount(session.host, parentId, account._id, session.dismiss);
    },
    addLinkedAccount: () => {
      void showCreateLinkedAccount(session.host);
    },
    switchToParent: () => {
      const parentId = session.stateRef.current.user?.parent_id;
      if (!parentId) {
        return;
      }
      void switchToParentAccount(session.host, parentId, session.dismiss);
    },
    previewWaiver: () => {
      void showStudioWaiver(session.host, session.stateRef.current.user);
    },
    openDocument: (document: ProfileDocumentRow) => {
      openAgreement(session.host, document.documentUrl, document.name);
    },
    addOrReplacePayment: (method: ProfilePaymentMethodKind) => {
      void showPaymentCollector(session.host, method, () => {
        session.replaceProfileFromUser(asUser(session.host.getCurrentUser()));
      });
    },
    deletePayment: (method: ProfilePaymentMethodKind) => {
      const user = session.stateRef.current.user;
      if (!user?._id) {
        return;
      }
      void confirmDeletePayment(session.host, user, method, () => {
        void reloadMember(session.host, user._id as string).then((member) => {
          if (member) {
            session.replaceProfileFromUser(member);
          }
        });
      });
    },
  };
}

function buildProfileActions(session: ProfileSession): ProfileViewActions {
  return {
    ...navigationActions(session),
    ...editActions(session),
    ...membershipActions(session),
    ...accountActions(session),
  };
}

export function useProfile(data: OrchestratedProfileDialogData): ProfileDialogData {
  const host = data.host;
  const [state, setState] = useState<ProfileViewState>(() => emptyState(data.embedInHostModal));
  const stateRef = useRef(state);
  stateRef.current = state;
  const transactionPage = useRef(1);
  const spotFlagRef = useRef(false);
  const previewUrl = useRef<string | null>(null);
  const imageFile = useRef<File | null>(null);
  const imageVersion = useRef(1);
  const sessionRef = useRef<ProfileSession | null>(null);

  const patch = useCallback((partial: Partial<ProfileViewState>) => {
    setState((current) => ({ ...current, ...partial }));
  }, []);
  const replaceProfileFromUser = useCallback((user: ProfileRuntimeUser | null) => {
    replaceStoredProfile(user, previewUrl, imageVersion, patch);
  }, [patch]);
  const loadBookings = useCallback((userId: string) => {
    return loadProfileBookings(host, userId, spotFlagRef, patch);
  }, [host, patch]);
  const loadMemberships = useCallback((branchId: string, userId: string) => {
    return loadProfileMemberships(host, branchId, userId, patch);
  }, [host, patch]);
  const loadTransactions = useCallback((userId: string, replace: boolean) => {
    return loadProfileTransactions({ host, userId, replace, transactionPage, setState });
  }, [host]);
  const dismiss = useCallback((result: ProfileCloseResult) => {
    data.onDismiss(result);
  }, [data]);
  const refresh = useCallback(() => {
    const session = sessionRef.current;
    if (!session) {
      return;
    }
    refreshProfile(session);
  }, []);

  const session: ProfileSession = {
    host,
    stateRef,
    patch,
    setState,
    previewUrl,
    imageFile,
    imageVersion,
    dismiss,
    refresh,
    replaceProfileFromUser,
    loadMemberships,
    loadTransactions,
    loadBookings,
    transactionPage,
    spotFlagRef,
  };
  sessionRef.current = session;
  useEffect(() => subscribeProfile(session), [host, refresh, loadBookings, loadMemberships]);
  return {
    ...projectProfileDialogData(state, buildProfileActions(session), host),
    onClose: () => dismiss({ success: false, redirect: false }),
  };
}
